import { startCase } from 'lodash';
import { bigQuery, mixpanel } from '@fiverr-private/obs';
import { STEPS } from '../constants';
import * as eventsTypes from '../constants/biEvents';
import { TemporalUnit } from '../../types';

const getNumberOfWords = (message) => (message || '').trim().split(' ').length;

const pageCommonData = ({ pageCtxId, refCtxId, pageName }) => ({
    page: {
        ctx_id: pageCtxId,
        referrer: { ctx_id: refCtxId },
        element: {
            name: pageName
        }
    }
});

const commonData = (params) => {
    const { userId, sellerId, gigId, isOnline, userGuid, eventSource, subCategoryId, organizationId } = params;

    return {
        user: { id: userId },
        buyer: { id: userId },
        seller: { id: sellerId },
        gig: { id: gigId },
        uid: userGuid,
        entity: 'client',
        online: { is_online: isOnline },
        group: 'user_action',
        action: { type: 'click' },
        event_source: eventSource,
        sub_category: { id: subCategoryId },
        ...(organizationId ? { organization: { id: organizationId } } : {}),
        ...pageCommonData(params)
    };
};

const fmpShownEventData = (params) => {
    const data = commonData(params);
    const action  = params.action || 'pop_up';
    return ({
        ...data,
        group: 'first_message_popup_impressions',
        action: { type: action },
        page: {
            ...data.page,
            name: params.pageName
        },
        response: {
            unit: TemporalUnit.HOUR,
            value: params.responseTimeInHrs
        }
    });
};

const clickQuickResponseEventData = (params) => {
    const data = commonData(params);

    return ({
        ...data,
        page: {
            ...data.page,
            element: {
                ...data.page.element,
                text: params.message,
                step: params.step,
                metadata: JSON.stringify(params.metadata)
            }
        }
    });
};

const clickedOnAddAttachmentEventData = (params) => {
    const data = commonData(params);

    return {
        ...data,
        page: {
            ...data.page,
            name: params.pageName
        }
    };
};

const clickedOnSendMessageEventData = (params) => {
    const data = commonData(params);
    const elementName = params.elementName;
    return {
        ...data,
        page: {
            ...data.page,
            name: params.pageName,
            element: {
                ...data.page.element,
                ...elementName && { name: elementName },
                number_of_words: getNumberOfWords(params.message)
            }
        }
    };
};

const warningMessageShownEventData = (params) => {
    const data = commonData(params);

    return {
        ...data,
        entity: 'business',
        group: 'technical_events',
        action: undefined,
        type: 'message.error',
        reason: params.reason
    };
};

const clickXButtonEventData = (params) => {
    const common = commonData(params);
    const { page } = common;

    return {
        ...common,
        event_source: params.isSent ? 'message_sent_modal' : params.eventSource,
        page: {
            ...page,
            element: {
                ...page.element,
                ...(params.currentStep === STEPS.OFFER_MORE_GIGS ? { metadata: 'offer_more_gigs' }: {})
            }
        }
    };
};

const offerMoreGigsIsShowEventData = (params) => {
    const data = commonData(params);

    return ({
        ...data,
        action: {},
        page: {
            ...data.page,
            name: params.pageName,
            element: {
                ...data.page.element,
                id: params.baseMessage.id,
                type: params.baseMessage.text,
                metadata: params.gigIds.join(',')
            }
        },
        group: 'user_impressions'
    });
};

const clickSendMoreGigsEventData = (params) => {
    const data = commonData(params);

    return ({
        ...data,
        page: {
            ...data.page,
            name: params.pageName,
            element: {
                ...data.page.element,
                metadata: params.selectedGigIds.join(',')
            }
        }
    });
};

const messageSentModalIsShownEventData = (params) => {
    const data = commonData(params);

    return {
        ...data,
        action: {
            type: 'impression'
        },
        page: {
            ...data.page,
            name: params.pageName
        },
        group: 'user_impressions'
    };
};

const clickedOnViewMessageData = (params) => {
    const data = commonData(params);

    return {
        ...data,
        page: {
            ...data.page,
            name: params.pageName
        }
    };
};

const quickQuestionsViewIsShown = (params) => {
    const data = commonData(params);

    return {
        ...data,
        action: {
            type: 'impression'
        },
        group: 'user_impressions',
        event_source: params.source
    };
};

export const clickOnSubmitRequestButtonEventData = (params) => ({
    ...commonData(params),
    group: 'user_actions',
    action: {
        type: 'click'
    },
    page: {
        name: 'gig_page',
        element: {
            type: 'button',
            name: 'submit_a_service_request',
            text: 'Submit request'
        }
    },
    sub_group: 'seller_plus_events'
});

export const requestToOrderModalShownEventData = (params) => ({
    ...commonData(params),
    group: 'user_impressions',
    action: {
        type: 'impression'
    },
    page: {
        name: 'gig_page',
        element: {
            type: 'request_modal',
            name: 'submit_a_service_request',
            text: 'Submit request'
        }
    },
    sub_group: 'seller_plus_events'
});

export const briefButtonIsShown = (params) => {
    const data = commonData(params);

    return {
        ...data,
        action: {
            type: 'imp'
        },
        page: {
            ...data.page,
            name: params.pageName,
            element: {
                name: 'button'
            },
            component: {
                name: 'contact_seller_drawer'
            }
        },
        group: 'user_impressions',
        sub_group: 'fiverr_pro',
        feature: {
            name: 'fiverr_pro'
        },
        buyer: {
            ...data.buyer,
            request: {
                category_id: params.categoryId,
                subcategory_id: params.subCategoryId
            }
        }
    };
};

export const satisfactionGuaranteeIsShown = (params) => {
    const data = commonData(params);

    return {
        ...data,
        action: {
            type: 'imp'
        },
        page: {
            ...data.page,
            name: params.pageName,
            element: {
                name: params.elementName
            }
        },
        group: 'user_impressions',
        sub_group: 'fiverr_pro',
        feature: {
            name: 'fiverr_pro'
        }
    };
};

const getEventDataByType = (type, params) => {
    switch (type) {
        case eventsTypes.FIRST_MESSAGE_POPUP_IS_SHOWN:
            return fmpShownEventData(params);
        case eventsTypes.CLICKED_ON_ADD_ATTACHMENT_TO_MESSAGE:
            return clickedOnAddAttachmentEventData(params);
        case eventsTypes.CLICKED_ON_SEND_MESSAGE:
            return clickedOnSendMessageEventData(params);
        case eventsTypes.CLICKED_ON_QUICK_RESPONSE:
            return clickQuickResponseEventData(params);
        case eventsTypes.WARNING_MESSAGE_SHOW:
            return warningMessageShownEventData(params);
        case eventsTypes.CLICKED_ON_X_BUTTON:
            return clickXButtonEventData(params);
        case eventsTypes.POST_FIRST_MESSAGE_POP_UP_IS_SHOWN:
            return offerMoreGigsIsShowEventData(params);
        case eventsTypes.CLICK_SEND_MESSAGE_POST_FIRST_MESSAGE_POP_UP:
            return clickSendMoreGigsEventData(params);
        case eventsTypes.CLICK_ON_SUBMIT_REQUEST_BUTTON:
            return clickOnSubmitRequestButtonEventData(params);
        case eventsTypes.REQUEST_TO_ORDER_MODAL_IS_SHOWN:
            return requestToOrderModalShownEventData(params);
        case eventsTypes.APP_DOWNLOAD_MODAL_IS_SHOWN:
            return messageSentModalIsShownEventData(params);
        case eventsTypes.MESSAGE_SENT_MODAL_IS_SHOWN:
            return messageSentModalIsShownEventData(params);
        case eventsTypes.CLICKED_ON_VIEW_MESSAGE:
            return clickedOnViewMessageData(params);
        case eventsTypes.QUICK_QUESTIONS_VIEW_IS_SHOWN:
            return quickQuestionsViewIsShown(params);
        case eventsTypes.BRIEF_BUTTON_IS_SHOWN:
            return briefButtonIsShown(params);
        case eventsTypes.SATISFACTION_GUARANTEE_IS_SHOWN:
            return satisfactionGuaranteeIsShown(params);
        default:
            return commonData(params);
    }
};

export const sendBigQueryEvents = ({ type, params }) => {
    const event = getEventDataByType(type, params);

    bigQuery.send({ type, ...event });
};

export const sendMixpanelEvents = ({ type, params }) => {
    const event = getEventDataByType(type, params);
    let mixpanelEnrichment;

    switch (type) {
        case eventsTypes.CLICKED_ON_SEND_MESSAGE:
            mixpanelEnrichment = {
                'Attachments amount': params.attachmentsCount,
                'Number of words': getNumberOfWords(params.message)
            };
            break;
        case eventsTypes.CLICK_SEND_MESSAGE_POST_FIRST_MESSAGE_POP_UP:
            mixpanelEnrichment = {
                'Number of sellers': (params.selectedGigIds || []).length,
                'Is original message': !!params.isOriginalMessage
            };
            break;
        case eventsTypes.POST_FIRST_MESSAGE_POP_UP_IS_SHOWN:
            mixpanelEnrichment = {
                'Contains seller name': !!params.messageContainsSellerName
            };
            break;
        case eventsTypes.CLICK_TOGGLE_MESSAGE_POST_FIRST_MESSAGE_POP_UP:
            mixpanelEnrichment = {
                'is shown': !!params.isShown
            };
            break;
        case eventsTypes.QUICK_QUESTIONS_VIEW_IS_SHOWN:
            mixpanelEnrichment = {
                type: 'First message popup - quick question is shown',
                'Source': startCase(params.source)
            };
            break;
        default:
            mixpanelEnrichment = {};
    }

    const reportedType = mixpanelEnrichment.type || type;

    mixpanel.track(reportedType, {
        ...event,
        ...mixpanelEnrichment
    });
};
